import React from "react";
import PageIntro from "../../../components/admin/PageIntro";
import SectionCard from "../../../components/admin/SectionCard";
import StatCard from "../../../components/data-display/StatCard";
import InfoCard from "../../../components/admin/InfoCard";
import { adminTheme } from "../../../components/admin/adminTheme";
import { useMdDashboard } from "../hooks";
import type { MdDashboardResponseDto } from "../md.types";

const MdDashboardPage: React.FC = () => {
  const { data, isLoading, error } = useMdDashboard();

  if (isLoading) {
    return (
      <PageIntro
        title="MD/CEO Control Room"
        message="Loading platform-wide overview…"
      />
    );
  }

  if (error || !data) {
    return (
      <PageIntro
        title="MD/CEO Control Room"
        message="Unable to load dashboard data. Please try again."
      />
    );
  }

  const dashboard: MdDashboardResponseDto = {
    kpis: {
      activeProviders: data.kpis?.activeProviders ?? 0,
      bookingsToday: data.kpis?.bookingsToday ?? 0,
      bookingsWeek: data.kpis?.bookingsWeek ?? 0,
      completed: data.kpis?.completed ?? 0,
      noShows: data.kpis?.noShows ?? 0,
      cancellations: data.kpis?.cancellations ?? 0,
      platformRevenue: data.kpis?.platformRevenue ?? "0",
      refunds: data.kpis?.refunds ?? "0",
      netMargin: data.kpis?.netMargin ?? "0",
    },
    operations: {
      requested: data.operations?.requested ?? 0,
      awaitingAcceptance: data.operations?.awaitingAcceptance ?? 0,
      accepted: data.operations?.accepted ?? 0,
      autoAccepted: data.operations?.autoAccepted ?? 0,
      inService: data.operations?.inService ?? 0,
      completed: data.operations?.completed ?? 0,
      noShow: data.operations?.noShow ?? 0,
      disputed: data.operations?.disputed ?? 0,
      noShowRate: data.operations?.noShowRate ?? 0,
      providersOverThreshold: data.operations?.providersOverThreshold ?? 0,
    },
    finance: {
      commission: data.finance?.commission ?? "0",
      subscriptions: data.finance?.subscriptions ?? "0",
      depositMargin: data.finance?.depositMargin ?? "0",
      refunds: data.finance?.refunds ?? "0",
      recovered: data.finance?.recovered ?? "0",
      recoveryGap: data.finance?.recoveryGap ?? "0",
    },
    audit: {
      areaCount: data.audit?.areaCount ?? 0,
      districtCount: data.audit?.districtCount ?? 0,
      businessHeadCount: data.audit?.businessHeadCount ?? 0,
      mdCount: data.audit?.mdCount ?? 0,
      slaRiskCount: data.audit?.slaRiskCount ?? 0,
    },
    hr: {
      totalStaff: data.hr?.totalStaff ?? 0,
      vacancies: data.hr?.vacancies ?? 0,
      newHires: data.hr?.newHires ?? 0,
      exits: data.hr?.exits ?? 0,
    },
    it: {
      frontendUptime: data.it?.frontendUptime ?? 0,
      apiUptime: data.it?.apiUptime ?? 0,
      errorRate: data.it?.errorRate ?? 0,
    },
    grievance: {
      newComplaints: data.grievance?.newComplaints ?? 0,
      openComplaints: data.grievance?.openComplaints ?? 0,
      slaResolutionRate: data.grievance?.slaResolutionRate ?? 0,
      avgResolutionTime: data.grievance?.avgResolutionTime ?? "0",
    },
    approvals: {
      pricingRequests: data.approvals?.pricingRequests ?? 0,
      policyRequests: data.approvals?.policyRequests ?? 0,
      disputeDecisions: data.approvals?.disputeDecisions ?? 0,
      highRiskProviders: data.approvals?.highRiskProviders ?? 0,
      financeExceptions: data.approvals?.financeExceptions ?? 0,
      auditRedFlags: data.approvals?.auditRedFlags ?? 0,
    },
  };

  const { kpis, operations, finance, audit, hr, it, grievance, approvals } =
    dashboard;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: adminTheme.space.section,
        padding: adminTheme.space.section,
        background: adminTheme.color.pageBg,
        minHeight: "100%",
      }}
    >
      <PageIntro
        title="MD/CEO Control Room"
        message="Platform-level health, risk and governance across all departments."
      />

      <SectionCard title="Global KPIs">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: adminTheme.space.lg,
          }}
        >
          <StatCard label="Active providers" value={kpis.activeProviders} />
          <StatCard
            label="Bookings (today / week)"
            value={`${kpis.bookingsToday} / ${kpis.bookingsWeek}`}
            helpText={`Completed: ${kpis.completed}, No-shows: ${kpis.noShows}, Cancellations: ${kpis.cancellations}`}
          />
          <StatCard
            label="Platform revenue (month)"
            value={kpis.platformRevenue}
            helpText={`Refunds: ${kpis.refunds}, Net margin: ${kpis.netMargin}`}
          />
        </div>
      </SectionCard>

      <SectionCard title="Operations – bookings & provider performance">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: adminTheme.space.md,
          }}
        >
          <InfoCard
            title="Booking funnel"
            body={`Requested: ${operations.requested}, Awaiting acceptance: ${operations.awaitingAcceptance}, Accepted: ${operations.accepted}, Auto-accepted: ${operations.autoAccepted}, In service: ${operations.inService}, Completed: ${operations.completed}, No-show: ${operations.noShow}, Disputed: ${operations.disputed}.`}
          />
          <InfoCard
            title="Risk signals"
            body={`Customer no-show rate: ${operations.noShowRate}%, Providers breaching cancellation limits: ${operations.providersOverThreshold}.`}
          />
        </div>
      </SectionCard>

      <SectionCard title="Finance & accounts – CFO oversight">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: adminTheme.space.md,
          }}
        >
          <InfoCard
            title="Revenue breakdown"
            body={`Commission: ${finance.commission}, Subscriptions: ${finance.subscriptions}, Deposit margin: ${finance.depositMargin}.`}
          />
          <InfoCard
            title="Refunds & recovery"
            body={`Refunds: ${finance.refunds}, Recovered: ${finance.recovered}, Recovery gap: ${finance.recoveryGap}.`}
          />
        </div>
      </SectionCard>

      <SectionCard title="Audit & disputes – oversight & escalation">
        <InfoCard
          title="Dispute pipeline"
          body={`Area: ${audit.areaCount}, District: ${audit.districtCount}, Business head: ${audit.businessHeadCount}, MD level: ${audit.mdCount}, SLA at risk: ${audit.slaRiskCount}.`}
        />
      </SectionCard>

      <SectionCard title="HR – people & compliance">
        <InfoCard
          title="Staffing overview"
          body={`Total staff: ${hr.totalStaff}, Vacancies: ${hr.vacancies}, New hires: ${hr.newHires}, Exits: ${hr.exits}.`}
        />
      </SectionCard>

      <SectionCard title="IT – system health & data">
        <InfoCard
          title="System status"
          body={`Frontend uptime: ${it.frontendUptime}%, API uptime: ${it.apiUptime}%, Error rate (24h): ${it.errorRate}%.`}
        />
      </SectionCard>

      <SectionCard title="Grievance & feedback – complaints & trust">
        <InfoCard
          title="Complaints & SLA"
          body={`New complaints (month): ${grievance.newComplaints}, Open complaints: ${grievance.openComplaints}, Resolution rate within SLA: ${grievance.slaResolutionRate}%, Avg resolution time: ${grievance.avgResolutionTime}.`}
        />
      </SectionCard>

      <SectionCard title="Approvals & alerts">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: adminTheme.space.md,
          }}
        >
          <InfoCard
            title="Pending approvals"
            body={`Pricing changes: ${approvals.pricingRequests}, Policy changes: ${approvals.policyRequests}, High-impact disputes: ${approvals.disputeDecisions}.`}
          />
          <InfoCard
            title="Critical alerts"
            body={`High-risk providers: ${approvals.highRiskProviders}, Finance exceptions: ${approvals.financeExceptions}, Audit red flags: ${approvals.auditRedFlags}.`}
          />
        </div>
      </SectionCard>
    </div>
  );
};

export default MdDashboardPage;